import {
    Component,
    ElementRef,
    forwardRef,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    AirspaceAuthorisationManager,
    airspaceAuthorisationTypesRequiringCurrentUserAsRpic,
    ExternalAuthorisationProvider,
    FlyFreelyAuthorisationProvider,
    mapSourceFiltersForResponse
} from '@flyfreely-portal-ui/airspace';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AirspaceAuthorisationDto,
    AirspaceAuthorisationService,
    AirspaceCheckDto,
    AirspaceJurisdictionDto,
    AttachmentHandler,
    BatterySetDto,
    BatterySetService,
    buildDisplayableMissionDocumentation,
    buildMissionRequiredDocumentation,
    calculateEditableSteps,
    calculateValidatedSteps,
    CombinedMissionStatus,
    combineFormResponses,
    ContactWithLocationDto,
    CurrentAttachmentVersionDto,
    DateFormatService,
    DateRange,
    DEFAULT_MAX_HEIGHT,
    defaultTimezone,
    DisplayableMissionDocumentation,
    DisplayableMissionDto,
    displayableMissionDtoCombinedStatus,
    DO_NOTHING,
    DownloadService,
    FEATURE_CASA_AUTHORISATION,
    FEATURE_EQUIPMENT,
    FEATURE_INCIDENT_REPORTS,
    FEATURE_LEGACY_MISSION_APPROVALS,
    FEATURE_LOTAM,
    FEATURE_NOTAMS,
    FEATURE_PROJECTS,
    FEATURE_WORKGROUPS,
    findActiveApprovals,
    findRpaModelDocumentationRequiringEntities,
    FlightLogFileDto,
    FlightLogsService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    FormResponseCommand,
    hasAnyPermission,
    hasFeatureFlag,
    InvalidOperation,
    isApprovalResolved,
    LocationDetailsDto,
    LocationFeatureDto,
    LocationService,
    LookupObject,
    MissionApprovalService,
    MissionCrewDetailsDto,
    MissionCrewDto,
    MissionDocumentationDto,
    MissionReportService,
    MissionRoleDto,
    MissionService,
    NameValue,
    NotamDto,
    NotFound,
    OperationForbidden,
    OperationsSummaryService,
    PersonDto,
    PersonService,
    PersonsOrganisationDto,
    PrintOption,
    ProjectsService,
    RequestersMissionApprovalDto,
    RequiringEntity,
    RpaDto,
    RpaTypeDto,
    RpaTypesService,
    RulesetDto,
    SimpleAuthorityDto,
    SimpleResourceDetails,
    SortieDto,
    StepDescription,
    StepEntityRequiredDocumentation,
    toLookup,
    unique,
    UpdateMissionFilingFieldsCommand,
    UserService,
    WorkGroupsService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FullScreenService } from '@flyfreely-portal-ui/fullscreen';
import { getFeatureGroups, toMapFeature } from '@flyfreely-portal-ui/locations';
import {
    FeatureGroup,
    MapDataInput,
    MapSourceFilters
} from '@flyfreely-portal-ui/map';
import { ExternalMissionsDataService } from '@flyfreely-portal-ui/missions-shared';
import { MissionNoticesDataService } from '@flyfreely-portal-ui/notices-shared';
import { OperationsSearchDataService } from '@flyfreely-portal-ui/operations-shared';
import { FormatRpaPipe } from '@flyfreely-portal-ui/resource-ui';
import { SitesDataService } from '@flyfreely-portal-ui/sites-shared';
import {
    FormatAuthorityPipe,
    FormatDateTimePipe,
    LocationBearing
} from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { bearing } from '@turf/bearing';
import { center } from '@turf/center';
import { distance } from '@turf/distance';
import { bearingToAzimuth } from '@turf/helpers';
import { Angulartics2 } from 'angulartics2';
import { deepEqual } from 'fast-equals';
import { Feature, LineString } from 'geojson';
import {
    AirspaceCheckService,
    findLocationFlightAreaPolygon
} from 'libs/airspace/src/lib/airspace-check/airspace-check.service';
import { AirspaceDialoguesService } from 'libs/airspace/src/lib/airspace-dialogues.service';
import { DocumentationActions } from 'libs/documentation/src/lib/interfaces';
import { EquipmentDialoguesService } from 'libs/equipment/src/lib/equipment-dialogues.service';
import { FlightLogDataService } from 'libs/flight-logs/src/lib/flight-log-data.service';
import { FlightLogWithDuration } from 'libs/flight-logs/src/lib/flight-log-list/flight-log-list-dialogue.component';
import { FlightLogsDialoguesService } from 'libs/flight-logs/src/lib/flight-logs-dialogues.service';
import { colourGenerator } from 'libs/map/src/lib/flyfreely-map/helpers';
import { SummaryHeader } from 'libs/missions/src/lib/summary-header/summary-header.component';
import { PersonnelDialoguesService } from 'libs/personnel/src/lib/personnel-dialogues.service';
import { RpaDialoguesService } from 'libs/rpa/src/lib/rpa-dialogues.service';
import * as moment from 'moment';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { TabDirective, TabsetComponent } from 'ngx-bootstrap/tabs';
import {
    BehaviorSubject,
    combineLatest,
    concat,
    firstValueFrom,
    of,
    Subject
} from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    toArray
} from 'rxjs/operators';
import {
    approvalRequiringEntities,
    findActiveMissionApprovals,
    findMissionAirspaceJurisdictionId,
    getSimplifiedAuthorityFromApproval,
    missionLocationsToFeatureCollection,
    missionNoticeSort,
    notamToFeatureCollection
} from '../helpers';
import { MissionApprovalWithAuthority } from '../interfaces';
import { MissionDialoguesService } from '../mission-dialogues.service';
import { MissionDetailsStatus } from './mission-details-status/mission-details-status.component';
import { MissionDetailsService } from './mission-details.service';

/**
 * Houses the flight log start and end times in a pre-rendered string to minimise the use of functions and pipes in the HTML
 */
interface SortieWithLogTimes extends SortieDto {
    logTimes?: string;
}

const SCREEN_IDENTIFIER = 'mission-details-v2-dialogue';

@Component({
    selector: 'mission-details-v2-dialogue',
    templateUrl: './mission-details-v2-dialogue.component.html',
    providers: [
        // forwardRef because MissionDetailsService imports MissionDialoguesService, which
        // imports this file, so it may not be defined yet when this file loads
        forwardRef(() => MissionDetailsService),
        FlightLogDataService,
        AirspaceCheckService,
        SitesDataService,
        FlyFreelyAuthorisationProvider,
        ExternalAuthorisationProvider,
        AirspaceAuthorisationManager,
        ExternalMissionsDataService,
        OperationsSearchDataService,
        MissionNoticesDataService,
        WorkTracker
    ]
})
export class MissionDetailsV2Dialogue implements OnInit, OnDestroy {
    missionId$ = new BehaviorSubject<number | undefined>(undefined);
    organisation: PersonsOrganisationDto;
    // FIXME: while this works there needs to be a better solution to pass this through the routing
    // It only works if every case where this should be false is explicitly stated
    allowEditSubmit = true;

    mission: DisplayableMissionDto;

    working = false;
    loaded = false;

    private ngUnsubscribe$ = new Subject<void>();

    approvalSteps: readonly NameValue[];
    missionDocumentationSteps: StepDescription[];
    location: LocationDetailsDto;
    actualLocation: LocationDetailsDto;
    missionDate: moment.Moment;

    locationFeatures: FeatureGroup[];

    // Holds filtered details of the mission
    missionDetails: {
        rpic: PersonDto;
        additionalCrew: MissionCrewDetailsDto[];
    };

    missionStartTime: string;
    missionEndTime: string;
    missionRuleset: RulesetDto;
    missionSorties: { date: string; flights: SortieWithLogTimes[] }[] = [];
    airspaceJurisdictionId: number;

    missionApprovals: MissionApprovalWithAuthority[] = [];
    activeMissionApprovals: MissionApprovalWithAuthority[] = [];
    missionAuthorities: SimpleAuthorityDto[] = [];

    flightLogsByFlight: { [flightId: number]: FlightLogWithDuration[] };
    flightPathByFlight: { [flightId: number]: Feature<LineString> } = {};
    unassignedFlightLogs: FlightLogFileDto[];
    flightLogIds$ = new BehaviorSubject<string[]>([]);

    craftLookup: { [rpaId: number]: RpaDto };
    personLookup: { [personId: number]: PersonDto };
    batterySetLookup: { [batterySetId: number]: BatterySetDto };
    rpaTypes: LookupObject<RpaTypeDto>;
    documentation: DisplayableMissionDocumentation;
    approvalRequiredDocumentation: {
        [approvalId: number]: StepEntityRequiredDocumentation;
    };
    attachments: CurrentAttachmentVersionDto[];
    requiringEntities: RequiringEntity[];
    // This will use a combination of entity type and entity Id as a key for the lookup
    // in order to prevent id overlap between the various sources
    requiringEntitySourceLookup: { [sourceIdentification: string]: string };
    canCancel: boolean;
    canEdit: boolean;
    hasEdit: boolean;
    canEditFieldAppOperator: boolean;
    fieldAppOperatorId: number | null = null;
    canComplete: boolean;
    hasFinalise: boolean;
    canFinalise: boolean;
    canUnfinalise: boolean;
    canPrepareToFly: boolean;
    canApprove: boolean;
    canAcceptApproval: boolean;
    canDelete: boolean;
    canViewOrganisation: boolean;
    canDoAirspaceAuthorisation: boolean;
    userCanViewAirspaceAuthorisation = false;
    useLegacyApprovals = false;

    checkingAirspace: boolean;
    checkingAuthorisation: boolean;
    modalStatus: 'ERROR' | 'LOADING' | 'NO_ACCESS' | 'NOT_FOUND' | 'LOADED' =
        'LOADING';
    approvedAirspaceAuthorisations: AirspaceAuthorisationDto[];
    enabledAuthorisationTypes = [
        AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE
    ];
    isUsingAlternativeAuthorisation = false;
    showAirspaceAuthorisationStatus = false;
    airspaceLoading = false;
    gcdVersion: string;

    shouldCheckAirspace$ = new Subject<void>();

    mapSourceFilters: MapSourceFilters;
    enabledMapLayerGroups: string[];

    hasFlightLogging: boolean;
    hasIncidentReports: boolean;
    canUseEquipment: boolean;
    canUseNotams: boolean;
    canUseLotams: boolean;
    canManageNotams = false;

    /**
     * Returns true both if there are relevant NOTAMs on the mission
     * or
     * if someone has declared "No relevant NOTAMs"
     */
    get hasNotams(): boolean {
        return (
            (this.mission?.notams != null && this.mission.notams.length > 0) ||
            (this.mission.noNotamDeclarationPerson != null &&
                this.mission.noNotamDeclarationTime != null)
        );
    }

    get hasLotams(): boolean {
        return (
            this.mission?.missionNoticeList != null &&
            this.mission.missionNoticeList.length > 0
        );
    }

    get canShowNotams(): boolean {
        return (
            this.canUseNotams &&
            (this.hasNotams ||
                [
                    CombinedMissionStatus.DRAFT,
                    CombinedMissionStatus.SUBMITTED,
                    CombinedMissionStatus.READY_TO_FLY
                ].some(status => this.combinedStatus === status))
        );
    }

    get canShowLotams(): boolean {
        return this.canUseLotams && this.hasLotams;
    }

    get missionNotams(): NotamDto[] {
        return this.mission.notams ?? [];
    }

    get locationJurisdiction(): AirspaceJurisdictionDto | undefined {
        return this.mission.location?.airspaceJurisdiction;
    }

    get existingAerodromes(): ContactWithLocationDto[] {
        return this.mission.aerodromesOfInterest ?? [];
    }

    featureFlags: LookupObject<string> = {};

    attachmentsHandler: AttachmentHandler;
    isEditingAttachments = false;
    documentationActions: DocumentationActions;
    printOptions: PrintOption[] = [];
    isCompleted: boolean;
    opened = false;
    isAddingNotams = false;

    cancelStack: (() => void)[] = [];

    combinedStatus: CombinedMissionStatus;

    mapData$ = new BehaviorSubject<MapDataInput>({});

    @ViewChild('map', { read: ElementRef, static: false }) map: ElementRef;
    @ViewChild('tabset') tabset: TabsetComponent;
    @ViewChild('missionRibbons') missionRibbons: MissionDetailsStatus;
    @ViewChild(SummaryHeader, { static: false }) summaryHeader: SummaryHeader;
    @ViewChild('approvalSelectionPopover', { static: false })
    approvalSelectionPopover: PopoverDirective;

    constructor(
        private missionService: MissionService,
        private missionApprovalService: MissionApprovalService,
        private locationService: LocationService,
        private userService: UserService,
        private personService: PersonService,
        private missionReportService: MissionReportService,
        private batterySetService: BatterySetService,
        private formatRpaPipe: FormatRpaPipe,
        private formatDateTimePipe: FormatDateTimePipe,
        private formatAuthorityPipe: FormatAuthorityPipe,
        public modal: BsModalRef<MissionDetailsV2Dialogue>,
        private commonDialoguesService: CommonDialoguesService,
        private logging: FlyFreelyLoggingService,
        private downloadService: DownloadService,
        private equipmentDialoguesService: EquipmentDialoguesService,
        private personnelDialoguesService: PersonnelDialoguesService,
        private rpaDialoguesService: RpaDialoguesService,
        @Inject(forwardRef(() => MissionDialoguesService))
        private missionDialoguesService: MissionDialoguesService,
        private airspaceDialoguesService: AirspaceDialoguesService,
        private fullScreenService: FullScreenService,
        private flightLogsService: FlightLogsService,
        private flightLogsDataService: FlightLogDataService,
        private flightLogsDialoguesService: FlightLogsDialoguesService,
        public missionDetailsService: MissionDetailsService,
        private airspaceCheckService: AirspaceCheckService,
        private airspaceAuthorisationService: AirspaceAuthorisationService,
        private airspaceAuthorisationManager: AirspaceAuthorisationManager,
        private operationsSummaryService: OperationsSummaryService,
        private rpaTypesService: RpaTypesService,
        private angulartics2: Angulartics2,
        private activatedRoute: ActivatedRoute,
        private workspaceStateService: WorkspaceStateService,
        private operationsSearchDataService: OperationsSearchDataService,
        private workTracker: WorkTracker,
        modalOptions: ModalOptions,
        private dateFormatService: DateFormatService,
        private projectsService: ProjectsService,
        private workGroupsService: WorkGroupsService
    ) {
        this.approvalSteps = missionApprovalService.getApprovalSteps();
        this.setupDocumentationActions();
        this.setupSubscriptions();

        this.airspaceAuthorisationManager.checkingAuthorisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(checking => (this.airspaceLoading = checking));

        modalOptions.closeInterceptor = () => {
            if (this.cancelStack.length > 0) {
                this.cancelStack[this.cancelStack.length - 1]();
                return Promise.reject();
            }
            if (this.summaryHeader?.message) {
                return this.commonDialoguesService.showConfirmationDialogue(
                    'Confirm Cancel',
                    `You have unsaved changes, are you sure you want to cancel?`,
                    'Yes',
                    () => Promise.resolve()
                );
            }
            return Promise.resolve();
        };
    }

    ngOnInit() {
        if (
            Object.prototype.hasOwnProperty.call(
                this.activatedRoute.snapshot.params,
                'missionId'
            )
        ) {
            this.missionId$.next(
                parseInt(this.activatedRoute.snapshot.params.missionId, 10)
            );
        }

        combineLatest([
            this.workspaceStateService.currentLoadedOrganisation$,
            this.missionId$.pipe(filter(id => id != null))
        ])
            .pipe(take(1), takeUntil(this.ngUnsubscribe$))
            .subscribe(([organisation, missionId]) => {
                this.organisation = organisation.organisation;

                this.modalStatus = 'LOADING';

                this.missionDetailsService.setup(
                    missionId,
                    this.organisation.id
                );
            });

        combineLatest([
            this.airspaceAuthorisationManager.missionAirspaceAuthorisations$,
            this.airspaceCheckService.result$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([authorisations, airspaceCheck]) => {
                this.setupExistingAuthorisationData(
                    authorisations,
                    airspaceCheck
                );
                if (authorisations != null) {
                    // Always show every current authorisation.
                    this.approvedAirspaceAuthorisations = authorisations;
                    this.showAirspaceAuthorisationStatus =
                        authorisations != null && authorisations.length > 0;
                }
            });

        this.airspaceAuthorisationManager.checkingAuthorisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(checking => (this.checkingAuthorisation = checking));

        this.refreshAirspacePermissions();
    }

    /**
     * Set the mission to show
     * @param missionId mission id to show
     */
    setup(missionId: number) {
        this.missionId$.next(missionId);
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.flightLogIds$.complete();
        this.mapData$.complete();
        this.shouldCheckAirspace$.complete();
    }

    private setupSubscriptions() {
        this.shouldCheckAirspace$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                if (
                    this.mission != null &&
                    (this.rpaTypes != null || this.canViewOrganisation == false)
                ) {
                    this.runAirspaceCheck();
                }
            });
        combineLatest([
            this.workTracker.observable,
            this.flightLogsDataService.working$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([working, gqlWorking]) =>
                    (this.working = working || gqlWorking)
            );

        this.missionDetailsService.error$.subscribe(error => {
            this.logging.error(error, error.message);
            if (error.fatal) {
                this.modalStatus = 'ERROR';
            }
        });

        combineLatest([
            this.missionService.change$.pipe(startWith(undefined)),
            this.missionDetailsService.change$.pipe(startWith(false)),
            this.missionApprovalService.change$.pipe(startWith(undefined)),
            this.airspaceAuthorisationService.change$.pipe(
                startWith(undefined)
            ),
            this.missionDetailsService.organisation$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([v1, transitionedToTerminalState, v3, v4, organisation]) => {
                    if (transitionedToTerminalState) {
                        this.modal.hide();
                    } else {
                        this.refreshData(organisation);
                    }
                }
            );

        this.airspaceCheckService.result$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(result => {
                const airspaceCheckSupport =
                    this.mission?.location?.airspaceJurisdiction
                        ?.airspaceCheckSupport;
                if (
                    this.canDoAirspaceAuthorisation ||
                    (airspaceCheckSupport != null &&
                        airspaceCheckSupport !==
                            AirspaceJurisdictionDto.AirspaceCheckSupport.NONE)
                ) {
                    this.refreshAirspaceAuthorisation(result);
                }
                this.setAirspaceLayers(result);
            });

        this.airspaceCheckService.resultStatus$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(status => {
                this.checkingAirspace = status === 'CHECKING';
            });

        this.flightLogsDataService.flightLogs$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(logs => this.setupFlightLogs(logs));

        // When the flight log IDs are passed in, analyse them in serial using concat.
        // switchMap is used on top of concat to ensure no flight logs are analysed unnecessarily.
        // This takes longer if done in serial, but having large numbers of complex flight logs was causing timeouts.
        // Due to how long this could take and the impact (it's only used for the map), it's not using the work tracker.
        this.flightLogIds$
            .pipe(
                switchMap(ids =>
                    concat(
                        ...ids.map((id, i) =>
                            this.flightLogsService
                                .findFlightLogSummaryById(
                                    this.flightLogsByFlight[id][0].id,
                                    this.organisation.id
                                )
                                .pipe(
                                    tap({
                                        next: flightSummary =>
                                            (this.flightPathByFlight[id] = {
                                                id: 'flightPath',
                                                type: 'Feature',
                                                properties: {},
                                                geometry:
                                                    flightSummary.summary
                                                        ?.flightPath
                                            }),
                                        error: (error: FlyFreelyError) =>
                                            this.logging.error(
                                                error,
                                                `Error while analysing flight log: ${error.message}`
                                            )
                                    })
                                )
                        )
                    ).pipe(toArray())
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(flightSummaries => this.setupMapFeatures());
        this.missionDetailsService.organisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => (this.organisation = organisation));
    }

    refreshData(organisation: PersonsOrganisationDto) {
        this.refreshMission(organisation);
        if (this.canViewOrganisation) {
            this.refreshRpaTypes(organisation);
        }
    }

    refreshRpaTypes(organisation: PersonsOrganisationDto) {
        this.rpaTypesService
            .findRpaTypes(organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: types => {
                    this.rpaTypes = types.reduce(toLookup, {});
                    this.shouldCheckAirspace$.next();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error finding RPA types: ${error.message}`
                    )
            });
    }

    private setupPermissions(organisation: PersonsOrganisationDto) {
        const currentUserId = this.userService.getCurrentUser().id;
        const isSystemAdmin =
            this.userService.getCurrentUser().type === 'SYSTEM_ADMIN';
        this.canEdit = this.mission.availableActions.canEdit;
        this.hasEdit = this.mission.availableActions.hasEdit;
        this.canEditFieldAppOperator =
            this.hasEdit &&
            (this.mission.status === DisplayableMissionDto.Status.DRAFT ||
                this.mission.status ===
                    DisplayableMissionDto.Status.READY_TO_FLY) &&
            this.mission.missionCrewDetails.some(
                c => c.role.coreRole !== 'PILOT_IN_COMMAND'
            );
        this.canComplete = this.mission.availableActions.canComplete;
        this.canPrepareToFly = this.mission.availableActions.canPrepareToFly;
        this.canFinalise = this.mission.availableActions.canFinalise;
        this.hasFinalise = this.mission.availableActions.hasFinalise;
        this.canUnfinalise = this.mission.availableActions.canUnfinalise;
        this.canApprove = this.mission.availableActions.canApprove;
        this.canAcceptApproval =
            this.mission.approvals != null &&
            this.mission.approvals[this.mission.approvals.length - 1]
                ?.status === 'APPROVED' &&
            this.mission.missionCrewDetails.find(
                crew =>
                    crew.person.id === currentUserId &&
                    crew.role.coreRole ===
                        MissionRoleDto.CoreRole.PILOT_IN_COMMAND
            ) != null;

        this.canCancel = this.mission.availableActions.canCancel;

        this.canDelete = this.mission.availableActions.canDelete;

        this.hasFlightLogging =
            organisation.featureFlags.indexOf('rpaLogCollection') !== -1;

        this.hasIncidentReports =
            organisation.featureFlags.indexOf(FEATURE_INCIDENT_REPORTS) !== -1;
        this.featureFlags['hasIncidentReports'] = FEATURE_INCIDENT_REPORTS;

        this.canUseEquipment =
            organisation.featureFlags.indexOf(FEATURE_EQUIPMENT) !== -1;

        this.canUseNotams =
            organisation.featureFlags.indexOf(FEATURE_NOTAMS) !== -1;
        this.featureFlags['canUseNotams'] = FEATURE_NOTAMS;

        this.canUseLotams = hasFeatureFlag(this.organisation, FEATURE_LOTAM);

        this.allowEditSubmit =
            this.allowEditSubmit &&
            (this.mission.status === DisplayableMissionDto.Status.DRAFT ||
                isSystemAdmin);

        const editableSteps =
            this.canEdit ||
            this.mission.status === DisplayableMissionDto.Status.READY_TO_FLY
                ? calculateEditableSteps(
                      this.mission.status,
                      this.canPrepareToFly,
                      this.canFinalise,
                      this.canEdit
                  )
                : [];

        const validatedSteps = calculateValidatedSteps(this.mission.status);

        this.missionDocumentationSteps = this.missionService.getMissionSteps(
            editableSteps,
            validatedSteps
        );

        this.canDoAirspaceAuthorisation =
            hasFeatureFlag(organisation, FEATURE_CASA_AUTHORISATION) &&
            hasFeatureFlag(
                this.userService.getPersonalOrganisation(),
                FEATURE_CASA_AUTHORISATION
            );

        this.useLegacyApprovals = hasFeatureFlag(
            organisation,
            FEATURE_LEGACY_MISSION_APPROVALS
        );

        this.canViewOrganisation = hasAnyPermission(organisation, [
            PersonsOrganisationDto.Permissions.ORGANISATION_VIEW
        ]);
        if (this.canViewOrganisation) {
            this.refreshRpaTypes(organisation);
        }
    }

    refreshAirspacePermissions() {
        // Some jurisdictions (e.g. USA LAANC) require the current user to be
        // the RPIC before they can view or request authorisations.  This
        // subscription evaluates that permission once the first check result
        // and any existing authorisations are available, then calls setup()
        // exactly once per unique (check, authorisations) pair.
        combineLatest([
            this.airspaceCheckService.completeCheck$,
            this.airspaceAuthorisationManager.missionAirspaceAuthorisations$
        ])
            .pipe(
                distinctUntilChanged(deepEqual),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([completedCheck, authorisations]) => {
                const requiresCurrentUserAsRpic =
                    completedCheck.result.availableAutomatedAuthorisationList.includes(
                        airspaceAuthorisationTypesRequiringCurrentUserAsRpic[
                            completedCheck.result.jurisdiction
                        ]
                    );

                // This assumes authorisations returned are for this person;
                // this logic can be revised here if that changes.
                this.userCanViewAirspaceAuthorisation =
                    !requiresCurrentUserAsRpic ||
                    (authorisations != null && authorisations.length > 0);

                if (this.userCanViewAirspaceAuthorisation) {
                    this.airspaceAuthorisationManager.setup(
                        [
                            AirspaceAuthorisationDto.AuthorisationType
                                .NZL_AIRSHARE,
                            AirspaceAuthorisationDto.AuthorisationType
                                .USA_LAANC,
                            AirspaceAuthorisationDto.AuthorisationType
                                .AUS_CASA_AUTHORISATION
                        ],
                        this.missionId$.getValue()
                    );
                }
            });
    }

    editMission() {
        this.missionDetailsService.organisation$
            .pipe(take(1))
            .subscribe(organisation => {
                this.missionDialoguesService.showMissionEditor(
                    this.mission,
                    organisation
                );
            });
    }

    private setupMission(
        mission: DisplayableMissionDto,
        organisation: PersonsOrganisationDto
    ): void {
        this.mission = mission;

        this.setupPermissions(organisation);

        this.airspaceAuthorisationManager.updateMission(mission);

        this.isCompleted =
            [
                DisplayableMissionDto.Status.COMPLETED,
                DisplayableMissionDto.Status.FINALISED
            ].find(s => s === this.mission.status) != null;

        this.combinedStatus = displayableMissionDtoCombinedStatus(mission);

        this.missionDate = moment(this.mission.missionDate).tz(
            this.mission.timeZone ?? this.organisation.timeZone
        );

        // This was being passed incorrect variables, it needs the timestamp, not the missionDate
        if (this.missionDate != null) {
            this.missionStartTime = moment(this.mission.missionDate)
                .tz(
                    this.mission.timeZone ??
                        this.organisation.timeZone ??
                        defaultTimezone
                )
                .startOf('day')
                .toISOString();
            this.missionEndTime = moment(this.mission.missionDate)
                .tz(
                    this.mission.timeZone ??
                        this.organisation.timeZone ??
                        defaultTimezone
                )
                .add(mission.missionEstimatedTime, 'seconds')
                .endOf('day')
                .toISOString();
            this.missionRuleset =
                mission.missionWorkflowVersion?.delegatedAuthority?.authorityType?.ruleset;
            const missionDateRange: DateRange = {
                start: this.missionStartTime,
                end: this.missionEndTime
            };
            this.operationsSearchDataService.find(
                missionDateRange,
                this.organisation.id
            );
        }

        if (this.mission.actualLocationId) {
            this.locationService
                .findLocation(
                    this.mission.actualLocationId,
                    this.organisation.id
                )
                .pipe(take(1), takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: location => {
                        this.actualLocation = location;
                    },
                    error: (error: FlyFreelyError) => {
                        this.logging.error(
                            error,
                            `Error finding mission location: ${error.message}`
                        );
                    }
                })
                .add(this.workTracker.createTracker());
        }

        this.craftLookup = this.mission.rpaLinkList
            .map(r => r.rpa)
            .reduce(toLookup, {});

        this.setupPersonLookup();

        this.setupMissionApprovals();

        this.setupSorties();

        this.setupAttachments();

        this.refreshAvailableReports();

        this.setupMapFeatures();

        this.refreshNearbyMissions();

        const airspaceCheckSupport =
            this.mission?.location?.airspaceJurisdiction?.airspaceCheckSupport;
        if (
            airspaceCheckSupport !==
            AirspaceJurisdictionDto.AirspaceCheckSupport.NONE
        ) {
            this.shouldCheckAirspace$.next();
        }
        /* if (
            airspaceCheckSupport != null &&
            airspaceCheckSupport !==
                AirspaceJurisdictionDto.AirspaceCheckSupport.NONE &&
            this.mission.airspaceAuthorisationList != null &&
            this.mission.airspaceAuthorisationList.length > 0
        ) {
            this.showAirspaceAuthorisationStatus = true;
            this.refreshAirspaceAuthorisation(null);
        } */

        const rpic = mission.missionCrewDetails.find(
            c => c.role.coreRole === 'PILOT_IN_COMMAND'
        );
        this.missionDetails = {
            rpic: rpic != null ? rpic.person : null,
            additionalCrew: mission.missionCrewDetails.filter(
                c => c.role.coreRole !== 'PILOT_IN_COMMAND'
            )
        };
        this.fieldAppOperatorId = mission.fieldAppOperatorId ?? null;

        this.airspaceJurisdictionId =
            findMissionAirspaceJurisdictionId(mission);

        this.refreshDependencies();

        this.mapData$.next({
            ...this.mapData$.getValue(),
            NOTAM: notamToFeatureCollection(this.mission.notams)
        });

        this.setupNotamPermissions();
        this.sortNotams();
        this.mission.missionNoticeList.sort(missionNoticeSort);
    }

    setupNotamPermissions() {
        this.canManageNotams =
            (this.canEdit &&
                (this.combinedStatus === CombinedMissionStatus.DRAFT ||
                    this.combinedStatus === CombinedMissionStatus.SUBMITTED)) ||
            (this.canComplete &&
                this.mission?.type === DisplayableMissionDto.Type.STANDARD &&
                this.combinedStatus !== CombinedMissionStatus.COMPLETED &&
                this.combinedStatus !== CombinedMissionStatus.FINALISED);
    }

    setupPersonLookup() {
        const missionCrewIds = this.mission.missionCrewDetails
            .map(c => c.person?.id)
            .filter(id => id != null);

        const sortiePilotIds = this.mission.sorties
            .map(sortie => sortie.pilotId)
            .filter(id => id != null);

        const idsToFetch = sortiePilotIds.filter(
            id => !missionCrewIds.includes(id)
        );
        if (idsToFetch.length === 0) {
            this.personLookup = this.mission.missionCrewDetails
                .map(c => c.person)
                .reduce(toLookup, {});
        } else {
            this.personService
                .findPersonnelGraphQL(
                    this.mission.organisationId ?? this.organisation.id,
                    0,
                    idsToFetch.length,
                    { idList: idsToFetch }
                )
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(result => {
                    const people =
                        result.results?.map<PersonDto>(person => ({
                            ...person,
                            activeJurisdictions: [],
                            defaultOrganisationId:
                                this.mission.organisationId ??
                                this.organisation.id,
                            personalOrganisationId: null
                        })) ?? [];
                    this.personLookup = this.mission.missionCrewDetails
                        .map(c => c.person)
                        .concat(people)
                        .reduce(toLookup, {});
                })
                .add(this.workTracker.createTracker());
        }
    }

    setupMissionApprovals() {
        const approvals = this.mission.approvals;
        if (approvals == null || approvals.length === 0) {
            this.missionApprovals = [];
            this.activeMissionApprovals = [];
            return;
        }

        const workflowVersion = this.mission.missionWorkflowVersion;
        // Every authority that an approval can belong to is already on the
        // mission, either via the workflow it is using, or as an additional
        // authority that has been attached to the mission.
        this.missionAuthorities = [
            workflowVersion?.delegatedAuthority,
            ...(workflowVersion?.additionalAuthorities ?? []),
            ...(this.mission.additionalAuthorities ?? [])
        ].filter(authority => authority != null);

        this.missionApprovals = approvals.map(approval => {
            const authority = this.missionAuthorities.find(
                auth => approval.authorityId === auth.id
            );
            return {
                approval,
                authority: authority ?? null,
                authorityType: authority?.authorityType ?? null
            };
        });
        this.activeMissionApprovals = findActiveMissionApprovals(
            this.missionApprovals,
            true
        );
    }

    private refreshDependencies() {
        this.refreshBatterySets();

        this.refreshDocumentation();

        this.refreshFlightLogs();
        this.modalStatus = 'LOADED';
    }

    private refreshBatterySets() {
        const doneWorking = this.workTracker.createTracker();
        Promise.all(
            this.mission.sorties
                .map(s => s.batterySetId)
                .filter(bsid => bsid !== null)
                .filter(unique)
                .map(bsid =>
                    firstValueFrom(
                        this.batterySetService.findBatterySet(
                            bsid,
                            this.mission.organisationId
                        )
                    ).catch((error: FlyFreelyError) =>
                        this.logging.error(
                            error,
                            `Error finding battery sets: ${error.message}`
                        )
                    )
                )
        )
            .then((batterySets: BatterySetDto[]) => {
                this.batterySetLookup = batterySets.reduce(toLookup, {});
                doneWorking();
            })
            .catch((error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error setting up battery sets: ${error.message}`
                );
                doneWorking();
            });
    }

    // TODO: this seems dated. Refactor to the updated format if not needed
    private refreshDocumentation() {
        const missionId = this.missionId$.getValue();
        if (!missionId) {
            return;
        }
        this.missionService
            .findDocumentation(this.missionId$.getValue(), this.organisation.id)
            .subscribe({
                next: documentation => {
                    this.setupDocumentation(documentation);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while fetching mission documentation: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    private refreshFlightLogs() {
        const missionId = this.missionId$.getValue();
        if (
            missionId == null ||
            !this.hasFlightLogging ||
            !this.canViewOrganisation
        ) {
            return;
        }
        this.flightLogsDataService.findFlightLogs(
            0,
            // Can't do pagination on this
            1000000,
            null,
            {
                organisationId: this.organisation.id,
                missionId,
                flightLogCollectionSourceIdList: null,
                uploadedByIdList: null
            }
        );
    }

    setupFlightLogs(flights: FlightLogWithDuration[]) {
        this.flightLogsByFlight = flights
            .filter(f => f.flightId != null)
            .reduce(
                (acc, f) => ({
                    ...acc,
                    [f.flightId]:
                        f.flightId in acc ? acc[f.flightId].concat(f) : [f]
                }),
                {} as { [flightId: number]: FlightLogWithDuration[] }
            );

        this.unassignedFlightLogs = flights.filter(f => f.flightId == null);

        this.setupMapFeatures();

        const ids = Object.keys(this.flightLogsByFlight).filter(
            fid => this.flightLogsByFlight[fid].length !== 0
        );
        this.flightLogIds$.next(ids);
    }

    setupSorties() {
        if (this.mission.sorties.length === 0) {
            this.missionSorties = [];
            return;
        }
        this.missionDate = moment(this.mission.missionDate).tz(
            this.mission.timeZone
        );

        this.missionSorties = this.mission.sorties
            .map(s =>
                s.durationSource !== SortieDto.DurationSource.LOGS
                    ? { ...s }
                    : this.buildSortieLogTimes(s)
            )
            .reduce((acc, s) => {
                const date =
                    s.recordedStartTime != null
                        ? moment(s.recordedStartTime)
                              .tz(
                                  this.mission.timeZone ??
                                      this.organisation.timeZone
                              )
                              .format(this.dateFormatService.getDateFormat())
                        : '';
                if (acc.length === 0 || acc[acc.length - 1].date !== date) {
                    return [...acc, { date, flights: [s] }];
                }
                return [
                    ...acc.slice(0, -1),
                    { date, flights: [...acc[acc.length - 1].flights, s] }
                ];
            }, [] as typeof MissionDetailsV2Dialogue.prototype.missionSorties);
    }

    private buildSortieLogTimes(sortie: SortieDto): SortieWithLogTimes {
        if (sortie.recordedStartTime == null) {
            return { ...sortie };
        }
        const timeZone = this.mission.timeZone;
        const startTime = this.formatDateTimePipe.transform(
            sortie.recordedStartTime,
            timeZone
        );
        const endTime =
            sortie.recordedEndTime != null
                ? this.formatDateTimePipe.transform(
                      sortie.recordedEndTime,
                      timeZone
                  )
                : null;
        if (endTime != null) {
            return {
                ...sortie,
                logTimes: `${startTime} - ${endTime}`
            };
        } else {
            return {
                ...sortie,
                logTimes: `Start: ${startTime}`
            };
        }
    }

    setupAirspaceAuthorisationStatus(
        authorisation: AirspaceAuthorisationDto[]
    ) {
        if (authorisation == null) {
            this.showAirspaceAuthorisationStatus = false;
            return;
        }
        this.showAirspaceAuthorisationStatus = true;
        // Always show every current authorisation.
        this.approvedAirspaceAuthorisations = authorisation;
    }

    refreshAirspaceAuthorisation(airspaceCheck: AirspaceCheckDto) {
        const findLatestAuthorisation = (): AirspaceAuthorisationDto =>
            this.mission?.airspaceAuthorisationList.reduce(
                (acc, a: any) =>
                    moment(a.createTime).isAfter(moment(acc.createTime))
                        ? a
                        : acc,
                this.mission?.airspaceAuthorisationList[0]
            );
        const authorisationType =
            this.mission.airspaceAuthorisationList != null &&
            this.mission.airspaceAuthorisationList.length > 0
                ? findLatestAuthorisation().authorisationType
                : null;
        if (
            ((authorisationType ===
                AirspaceAuthorisationDto.AuthorisationType
                    .AUS_CASA_AUTHORISATION &&
                this.canDoAirspaceAuthorisation) ||
                authorisationType != null) &&
            this.mission?.airspaceAuthorisationList != null &&
            this.mission?.airspaceAuthorisationList.length > 0 &&
            this.mission?.id != null
        ) {
            this.airspaceAuthorisationManager.findAuthorisationsByMissionId(
                this.mission.id
            );
        }
    }

    setupExistingAuthorisationData(
        authorisations: AirspaceAuthorisationDto[],
        airspaceCheck?: AirspaceCheckDto
    ) {
        if (
            authorisations?.filter(a => a.status != null) !== null ||
            authorisations?.length > 0
        ) {
            // FIXME: this needs to be either hard-coded better, or done programatically
            this.gcdVersion =
                airspaceCheck?.airspaceDatasetList?.find(
                    s => s.identifier === 'AUS_GCD'
                )?.version ?? null;
            this.setupAirspaceOperatingArea(authorisations);
            this.setupAirspaceAuthorisationStatus(authorisations);
        }
    }

    showAirspaceAuthorisation() {
        this.angulartics2.eventTrack.next({
            action: 'view-authorisation',
            properties: {
                category: 'airspace-authorisation'
            }
        });
        const command = this.buildAirspaceCheckCommand();
        this.airspaceDialoguesService.showAirspaceAuthorisationDialogue(
            this.airspaceAuthorisationManager,
            this.airspaceCheckService,
            this.organisation,
            command,
            this.mission,
            null,
            null,
            this.rpaTypes,
            false
        );
    }

    buildAirspaceCheckCommand() {
        const rpicId = this.organisation.personalOrganisation
            ? this.organisation.ownerId
            : this.mission.missionCrewDetails
                  .filter(
                      c =>
                          c.role != null &&
                          c.person != null &&
                          c.role.coreRole ===
                              MissionRoleDto.CoreRole.PILOT_IN_COMMAND
                  )
                  .map(c => c.person.id)[0];

        // @ts-ignore bad type overlapping
        const missionFormValues: MissionFormModel = {
            ...this.mission,
            missionDate: new Date(this.mission?.missionDate),
            missionObjectivesGroup: {
                missionObjective: this.mission?.missionObjective,
                isDummy: this.mission?.isDummy,
                location: this.mission?.location,
                name: this.mission?.name,
                missionObjectiveTypeId: this.mission?.missionObjectiveType?.id
            },
            rpicId: rpicId,
            missionTypeId: this.mission?.missionType?.id,
            documentation: null,
            equipmentIds: this.mission?.equipment?.map(e => e.id),
            missionCrew: this.mission?.missionCrewDetails as MissionCrewDto[],
            missionWorkflowVersionId: this.mission?.missionWorkflowVersionId,
            crewNotes: this.mission?.crewNotes,
            timeZone: this.mission?.timeZone,
            missionEstimatedTime: this.mission?.missionEstimatedTime,
            timeOfDay: this.mission?.timeOfDay
        };

        return this.airspaceCheckService.setupCheckCommand(
            this.mission.crafts,
            [this.mission?.missionWorkflowVersion],
            missionFormValues,
            this.location ?? this.mission?.location,
            this.rpaTypes,
            true,
            false
        );
    }

    cancelAirspaceAuthorisation(authorisation: AirspaceAuthorisationDto) {
        this.angulartics2.eventTrack.next({
            action: 'cancel-authorisation',
            properties: {
                category: 'airspace-authorisation'
            }
        });
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Cancel Airspace Authorisation?',
                `Are you sure you want to cancel this airspace authorisation`,
                'Yes',
                () => Promise.resolve()
            )
            .then(() => {
                this.airspaceAuthorisationManager
                    .cancelAuthorisation(
                        authorisation.id,
                        authorisation.authorisationType
                    )
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe({
                        next: DO_NOTHING,
                        error: (error: FlyFreelyError) =>
                            this.logging.error(
                                error,
                                `Error while cancelling authorisation: ${error.message}`
                            )
                    })
                    .add(this.workTracker.createTracker());
            }, DO_NOTHING);
    }

    closeAirspaceAuthorisation(authorisation: AirspaceAuthorisationDto) {
        this.angulartics2.eventTrack.next({
            action: 'close-authorisation',
            properties: {
                category: 'airspace-authorisation'
            }
        });

        this.commonDialoguesService
            .showConfirmationDialogue(
                'Mission Airspace Authorisation',
                'Do you wish to close this airspace authorisation request?',
                'Yes',
                () =>
                    firstValueFrom(
                        this.airspaceAuthorisationService
                            .closeAuthorisation(authorisation.id)
                            .pipe(
                                tap({
                                    error: (error: FlyFreelyError) =>
                                        this.logging.error(
                                            error,
                                            `Error while closing authorisation: ${error.message}`
                                        )
                                })
                            )
                    )
            )
            .then(results => {
                this.approvedAirspaceAuthorisations = null;
            }, DO_NOTHING);
    }

    requestNewAirspaceAuthorisation() {
        this.approvedAirspaceAuthorisations = null;
        this.angulartics2.eventTrack.next({
            action: 'show-request-new-authorisation',
            properties: {
                category: 'airspace-authorisation'
            }
        });
        this.showAirspaceAuthorisation();
    }

    private setAirspaceLayers(airspaceCheck: AirspaceCheckDto) {
        if (airspaceCheck != null) {
            this.mapSourceFilters = mapSourceFiltersForResponse(airspaceCheck);
            const missionApprovalGcdVersion = this.gcdVersion;
            if (
                missionApprovalGcdVersion != null &&
                this.mapSourceFilters.AUS_GCD
            ) {
                this.mapSourceFilters.AUS_GCD = missionApprovalGcdVersion;
            }
            this.enabledMapLayerGroups = Object.keys(this.mapSourceFilters);
        }
    }

    setupAirspaceOperatingArea(
        approvedAirspaceAuthorisations: AirspaceAuthorisationDto[]
    ) {
        if (approvedAirspaceAuthorisations == null) {
            return;
        }
        approvedAirspaceAuthorisations.map(authorisation => {
            const area = authorisation.operatingArea;
            if (area != null) {
                const geometry = area != null ? <GeoJSON.Polygon>area : null;
                this.addFeature({
                    name: 'Operating Area',
                    type: LocationFeatureDto.Type.FLIGHT_AREA,
                    geometry: geometry
                } as LocationFeatureDto);
            }
        });
    }

    private addFeature(locationFeature: LocationFeatureDto) {
        if (this.locationFeatures == null) {
            this.locationFeatures = [];
        }
        let nextId =
            this.locationFeatures?.reduce(
                (acc, f) => (f.id > acc ? f.id : acc),
                0
            ) + 1;
        if (
            this.locationFeatures.length === 0 ||
            this.locationFeatures.filter(
                fg =>
                    fg?.categories?.findIndex(
                        c => c.id === locationFeature.type
                    ) !== -1
            ).length === 0
        ) {
            this.locationFeatures = this.locationFeatures.concat({
                id: nextId,
                name: locationFeature.name,
                type: 'Polygon',
                canAdd: false,
                categories: [
                    {
                        id: locationFeature.type,
                        name: locationFeature.name
                    }
                ],
                existingFeatures:
                    locationFeature != null
                        ? [toMapFeature(locationFeature, nextId++)]
                        : []
            });
        } else {
            this.locationFeatures = this.locationFeatures.map(fg => {
                const categoryMatch =
                    fg.categories?.findIndex(
                        c => c.id === locationFeature.type
                    ) ?? -1;
                return categoryMatch !== -1
                    ? {
                          ...fg,
                          existingFeatures: fg.existingFeatures.concat(
                              toMapFeature(locationFeature, nextId++)
                          )
                      }
                    : fg;
            });
        }
    }

    private runAirspaceCheck() {
        if (
            this.mission == null ||
            (this.rpaTypes == null && this.canViewOrganisation)
        ) {
            return;
        }
        const location = this.mission.location;
        if (location == null) {
            return;
        }

        const locationPolygon = findLocationFlightAreaPolygon(
            location.features
        );

        if (locationPolygon == null) {
            return;
        }

        const jurisdiction =
            this.mission.missionWorkflowVersion?.delegatedAuthority
                ?.authorityType?.jurisdiction?.identifier;
        const ruleset =
            this.mission.missionWorkflowVersion?.delegatedAuthority
                ?.authorityType?.ruleset?.identifier;

        if (jurisdiction == null || ruleset == null) {
            return;
        }

        const startTime = this.mission.missionDate;
        const endTime = moment(startTime)
            .add(this.mission.missionEstimatedTime, 'seconds')
            .toISOString();

        const airspaceCheckCommand = {
            startTime: startTime,
            endTime: endTime,
            jurisdiction: jurisdiction,
            location: locationPolygon,
            rpaList: this.canViewOrganisation
                ? this.mission.crafts
                      .filter(
                          r =>
                              r.rpaTypeId != null &&
                              this.rpaTypes[r.rpaTypeId]
                                  ?.performanceSpecifications
                                  ?.maxTakeOffWeight != null
                      )
                      .map(r => ({
                          id: r.id,
                          mtow: this.rpaTypes[r.rpaTypeId]
                              .performanceSpecifications.maxTakeOffWeight,
                          serialNumber: r.manufacturerSerialNumber
                      }))
                : this.mission.crafts.map(r => ({
                      id: r.id,
                      serialNumber: r.manufacturerSerialNumber
                  })),
            ruleset: ruleset,
            nearestFeatureSearch: false,
            nearestFeatureSearchRadiusMeters: 400,
            maximumHeight: this.mission.maximumHeight ?? DEFAULT_MAX_HEIGHT
        };

        this.airspaceCheckService.updateSavedMission(this.mission);
        this.airspaceCheckService.startSingleCheck(
            airspaceCheckCommand,
            this.mission.missionWorkflowVersionId,
            this.mission.location?.airspaceJurisdiction
        );
    }

    private setupAttachments() {
        if (this.attachmentsHandler == null) {
            this.attachmentsHandler = this.missionService.attachmentHandler(
                this.missionId$.getValue(),
                this.organisation.id
            );
            this.attachmentsHandler.destroyOn(this.ngUnsubscribe$);
        }
    }

    private setupDocumentationActions() {
        this.documentationActions = {
            getFormTemplatePrintUrl: (checklistId: number, step: string) =>
                this.missionService.getFormTemplatePrintUrl(
                    this.missionId$.getValue(),
                    checklistId,
                    step,
                    this.organisation.id
                ),
            getFormResponsePrintUrl: (
                formId: number,
                step: string,
                relatedEntityType?: string,
                relatedEntityId?: number
            ) =>
                this.missionService.getFormResponsePrintUrl(
                    this.missionId$.getValue(),
                    formId,
                    step,
                    this.organisation.id,
                    relatedEntityType,
                    relatedEntityId
                ),
            storeFormResponse: (
                formId: number,
                step: string,
                response: FormResponseCommand
            ) =>
                firstValueFrom(
                    this.missionService
                        .updateDocumentation(this.missionId$.getValue(), {
                            [step]: [response]
                        })
                        .pipe(
                            tap(documentation =>
                                this.setupDocumentation(documentation)
                            ),
                            map(() => undefined)
                        )
                )
        };
    }

    private setupDocumentation(documentation: MissionDocumentationDto) {
        this.documentation = buildDisplayableMissionDocumentation(
            documentation,
            findActiveApprovals(this.mission.approvals ?? [], true),
            this.mission.crafts,
            this.formatRpaPipe.transform
        );

        this.requiringEntities = documentation.missionRequiringEntities
            .concat(
                findActiveApprovals(this.mission.approvals ?? []).reduce(
                    (acc, approval) =>
                        acc.concat(approvalRequiringEntities(approval)),
                    []
                )
            )
            .concat(
                findRpaModelDocumentationRequiringEntities(
                    documentation.rpaModelRequiredDocumentation
                )
            );

        const missionRequiringEntityLookup =
            documentation.missionRequiringEntities.reduce(
                (acc, entity) =>
                    acc[
                        `${entity.requiringEntityType}-${entity.requiringEntityId}`
                    ] != null ||
                    entity.requiringEntityType === 'MissionApproval'
                        ? acc
                        : {
                              ...acc,
                              [`${entity.requiringEntityType}-${entity.requiringEntityId}`]:
                                  'Mission Workflow'
                          },
                {}
            );

        const approvalRequiringEntityLookup = this.mission.approvals.reduce(
            (acc, approval) =>
                acc[`MissionApproval-${approval.id}`] != null
                    ? acc
                    : {
                          ...acc,
                          [`MissionApproval-${approval.id}`]:
                              this.missionApprovals?.find(
                                  app => app.approval.id === approval.id
                              ) != null
                                  ? this.formatAuthorityPipe.transform(
                                        getSimplifiedAuthorityFromApproval(
                                            this.missionApprovals.find(
                                                app =>
                                                    app.approval.id ===
                                                    approval.id
                                            ),
                                            this.organisation
                                        )
                                    )
                                  : 'Mission Approval'
                      },
            {}
        );

        const rpaWorkflowRequiringEntityLookup =
            findRpaModelDocumentationRequiringEntities(
                documentation.rpaModelRequiredDocumentation
            ).reduce(
                (acc, entity) =>
                    acc[
                        `${entity.requiringEntityType}-${entity.requiringEntityId}`
                    ] != null
                        ? acc
                        : {
                              ...acc,
                              [`${entity.requiringEntityType}-${entity.requiringEntityId}`]:
                                  'RPA Workflow'
                          },
                {}
            );
        this.requiringEntitySourceLookup = {
            ...missionRequiringEntityLookup,
            ...approvalRequiringEntityLookup,
            ...rpaWorkflowRequiringEntityLookup
        };
        this.approvalRequiredDocumentation = Object.keys(
            documentation.missionApprovalRequiredDocumentation
        ).reduce(
            (acc, approvalId) => ({
                ...acc,
                [approvalId]: buildMissionRequiredDocumentation(
                    documentation.missionApprovalRequiredDocumentation[
                        approvalId
                    ]
                )
            }),
            {}
        );
    }

    private setupMapFeatures() {
        if (this.mission == null) {
            this.locationFeatures = [];
            return;
        }

        const flightFeatures =
            this.flightLogsByFlight != null
                ? [
                      {
                          id: 1,
                          name: 'Flights',
                          canAdd: false,
                          type: 'LineString',
                          styles: {
                              line: [
                                  {
                                      paint: { 'line-color': ['get', 'color'] }
                                  }
                              ]
                          },
                          existingFeatures: this.mission.sorties
                              .filter(
                                  s => this.flightLogsByFlight[s.id] != null
                              )
                              .map(s => ({
                                  // FIXME it is only because the sortie IDs are large that there is no conflict.
                                  // This should be made correct
                                  id: s.id,
                                  name: `Flight ${s.number}`,
                                  properties: {
                                      color: colourGenerator(s.number)
                                  },
                                  geom:
                                      this.flightPathByFlight[s.id] != null
                                          ? this.flightPathByFlight[s.id]
                                                .geometry
                                          : {
                                                type: 'LineString',
                                                coordinates:
                                                    this.flightLogsByFlight[
                                                        s.id
                                                    ]
                                                        .filter(
                                                            f =>
                                                                f.summary !=
                                                                    null &&
                                                                f.summary
                                                                    .startLongitude !=
                                                                    null &&
                                                                f.summary
                                                                    .startLatitude !=
                                                                    null &&
                                                                f.summary
                                                                    .endLongitude !=
                                                                    null &&
                                                                f.summary
                                                                    .endLatitude !=
                                                                    null
                                                        )
                                                        .reduce(
                                                            (acc, f) =>
                                                                acc.concat([
                                                                    [
                                                                        f
                                                                            .summary
                                                                            .startLongitude,
                                                                        f
                                                                            .summary
                                                                            .startLatitude
                                                                    ],
                                                                    [
                                                                        f
                                                                            .summary
                                                                            .endLongitude,
                                                                        f
                                                                            .summary
                                                                            .endLatitude
                                                                    ]
                                                                ]),
                                                            []
                                                        )
                                            }
                              }))
                      } as FeatureGroup
                  ]
                : [];

        const flightFeaturesWithData = flightFeatures.filter(
            f => f.existingFeatures.length > 0
        );

        if (this.mission.location == null) {
            this.locationFeatures = [...flightFeaturesWithData];
        } else {
            const { features } = getFeatureGroups(
                this.mission.location.features,
                0,
                2
            );
            this.locationFeatures = features.concat(flightFeaturesWithData);
        }
    }

    refreshNearbyMissions() {
        const startTime = moment
            .tz(
                this.mission.missionDate,
                this.mission.timeZone ?? this.mission.location?.timeZone
            )
            .startOf('day')
            .toISOString();

        const endTime =
            this.mission.missionDate != null
                ? moment
                      .tz(
                          this.mission.missionDate,
                          this.mission.timeZone ??
                              this.mission.location?.timeZone
                      )
                      .endOf('day')
                      .toISOString()
                : null;

        this.operationsSummaryService
            .findMissionLocations({
                organisationId: this.organisation.id,
                startTime,
                endTime
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(missionLocationList => {
                if (missionLocationList == null) {
                    return;
                }
                const otherMissionLocations = missionLocationList.map(
                    missionLocation => ({
                        ...missionLocation,
                        // Filter the current mission from the locations, but not the current location
                        // This means that overlapping missions using the same location ID will still show if their times overlap.
                        missionList: missionLocation.missionList.filter(
                            mission => this.mission.id !== mission.id
                        )
                    })
                );
                const [pointLocations, polygonLocations] =
                    missionLocationsToFeatureCollection(
                        otherMissionLocations,
                        this.dateFormatService
                    );
                this.mapData$.next({
                    ...this.mapData$.getValue(),
                    MISSIONS: polygonLocations,
                    MISSION_MARKERS: pointLocations
                });
            });
    }

    showNotamSearch() {
        this.isAddingNotams = true;
        this.cancelStack.push(() => this.hideNotamSearch());
    }

    hideNotamSearch() {
        this.isAddingNotams = false;
        this.cancelStack.pop();
    }

    addAerodromeNotam(notam: NotamDto) {
        this.missionService
            .addNotam(
                this.mission.id,
                {
                    id: notam.id,
                    location: notam.location,
                    fir: notam.fir
                },
                false
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (notams: NotamDto[]) => {
                    this.mission.notams = notams;
                    this.logging.success('NOTAM added to mission');
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while adding NOTAM to mission: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());

        this.angulartics2.eventTrack.next({
            action: 'notam-add',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${notam.location}`
            }
        });
    }

    removeSearchNotam(notam: NotamDto) {
        this.missionService
            .removeNotam(
                this.mission.id,
                {
                    id: notam.id,
                    location: notam.location,
                    fir: notam.fir
                },
                false
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (notams: NotamDto[]) => {
                    this.mission.notams = notams;
                    this.logging.success('NOTAM removed from mission');
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while removing NOTAM from mission: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());

        this.angulartics2.eventTrack.next({
            action: 'notam-remove',
            properties: {
                category: SCREEN_IDENTIFIER,
                label: `${notam.location}`
            }
        });
    }

    acknowledgeNoRelevantNotam() {
        this.missionService
            .acknowledgeNoRelevantNotam(
                this.mission.id,
                this.userService.getCurrentUser().id
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () =>
                    this.logging.success(
                        'Declared no relevant NOTAM for mission'
                    ),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error declaring no relevant NOTAM: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    private sortNotams() {
        if (this.mission.location == null) {
            return;
        }
        const locationGeom = findLocationFlightAreaPolygon(
            this.mission.location.features
        );
        if (locationGeom == null) {
            return;
        }
        const centre = center(locationGeom);
        const getBearing = (pos: GeoJSON.Point) =>
            ({
                bearing: bearingToAzimuth(bearing(pos, centre)),
                distance: distance(centre, pos) * 0.539957
            } as LocationBearing);

        // sort notams to closest first
        this.mission.notams = this.mission.notams.sort(
            (a, b) =>
                getBearing(a.position).distance -
                getBearing(b.position).distance
        );
    }

    cancelMission() {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Cancel Mission',
                'Do you want to cancel this mission?',
                'Cancel Mission',
                () =>
                    this.missionService
                        .cancelMission(this.missionId$.getValue())
                        .catch((error: FlyFreelyError) => {
                            this.logging.error(
                                error,
                                `Error while cancelling mission: ${error.message}`
                            );
                            throw error;
                        })
            )
            .then(
                () => {
                    this.logging.success('Mission cancelled');
                    this.modal.hide();
                    doneWorking();
                },
                () => doneWorking()
            );
    }

    private refreshAvailableReports() {
        if (this.mission == null || !this.canViewOrganisation) {
            this.printOptions = [];
            return;
        }

        const missionId = this.missionId$.getValue();

        this.missionReportService
            .findAvailableTemplates(
                'MissionReport',
                missionId,
                this.organisation.id
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: reports => {
                    this.printOptions = reports.map(r => ({
                        name: r.name,
                        description: r.description,
                        print: () => {
                            const doneWorking =
                                this.workTracker.createTracker();
                            this.downloadService
                                .downloadFromUrl(
                                    this.missionReportService.getPdfReportUrl(
                                        r.reportTemplateId,
                                        missionId,
                                        this.organisation.id
                                    )
                                )
                                .catch(() => doneWorking())
                                .then(() => doneWorking());
                        }
                    }));
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error fetching mission reports: ${error.message}`
                    );
                }
            });
    }

    unfinaliseMission() {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Unfinalise Mission',
                'Do you want to unfinalise this mission?',
                'Unfinalise',
                () =>
                    this.missionService
                        .unfinaliseMission(this.missionId$.getValue())
                        .catch((error: FlyFreelyError) => {
                            this.logging.error(
                                error,
                                `Error while unfinalising mission: ${error.message}`
                            );
                            throw error;
                        })
            )
            .then(
                result => {
                    this.logging.success('Mission unfinalised');
                    doneWorking();
                },
                () => doneWorking()
            );
    }

    submitForApproval(missionApproval: RequestersMissionApprovalDto) {
        this.missionApprovalService
            .submitForApproval(missionApproval.id, [])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.logging.success('Mission submitted for approval');
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while submitting for approval: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    cancelApproval(missionApproval: RequestersMissionApprovalDto) {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Cancel Mission Approval Request',
                'Do you want to cancel this mission approval request?',
                'Cancel Mission Approval Request',
                () =>
                    firstValueFrom(
                        this.missionApprovalService
                            .cancelApproval(missionApproval.id)
                            .pipe(
                                tap({
                                    error: (error: FlyFreelyError) =>
                                        this.logging.error(
                                            error,
                                            `Error while cancelling approval: ${error.message}`
                                        )
                                })
                            )
                    )
            )
            .then(
                () => {
                    this.logging.success('Mission approval request cancelled');
                    doneWorking();
                },
                () => doneWorking()
            );
    }

    hasPendingApproval() {
        let pendingApproval = false;
        if (this.mission.approvals && this.mission.approvals.length > 0) {
            const status = RequestersMissionApprovalDto.Status;
            const nonPendingStatuses = [
                status.APPROVED,
                status.ACCEPTED,
                status.CANCELLED,
                status.DRAFT,
                status.REVOKED,
                status.REJECTED
            ];
            pendingApproval = !this.mission.approvals.some(approval =>
                nonPendingStatuses.includes(approval.status)
            );
        }
        return pendingApproval;
    }

    openMissionRecordDialogue() {
        this.missionDetailsService.organisation$
            .pipe(take(1))
            .subscribe(organisation =>
                this.missionDialoguesService.showMissionRecordDialogue(
                    this.mission,
                    organisation
                )
            );
    }

    openMissionCompletionDialogue() {
        this.missionDetailsService.organisation$
            .pipe(take(1))
            .subscribe(organisation =>
                this.missionDialoguesService.showMissionCompletion(
                    this.mission,
                    organisation
                )
            );
    }

    private refreshMission(organisation: PersonsOrganisationDto) {
        this.missionService
            .findMission(this.missionId$.getValue(), this.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: mission => {
                    this.setupMission(mission, organisation);
                },
                error: (error: FlyFreelyError) => {
                    if (
                        error instanceof NotFound ||
                        error instanceof InvalidOperation
                    ) {
                        this.modalStatus = 'NOT_FOUND';
                    } else if (error instanceof OperationForbidden) {
                        this.modalStatus = 'NO_ACCESS';
                    } else {
                        this.logging.error(
                            error,
                            `Error while fetching mission: ${error.message}`
                        );
                        this.modalStatus = 'ERROR';
                    }
                }
            })
            .add(this.workTracker.createTracker());
    }

    showSortieDocumentation(flight: SortieDto) {
        if (flight.id == null) {
            return;
        }
        this.missionService
            .findSortieDocumentation(
                this.missionId$.getValue(),
                flight.id,
                this.organisation.id
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: documentation => {
                    const formResponses = combineFormResponses([
                        documentation ? documentation.formResponses : {}
                    ]);

                    this.missionDialoguesService.showFlightDocumentationDialogue(
                        this.mission,
                        flight,
                        this.documentation.requiredDocumentation,
                        formResponses
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while fetching sortie documentation: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    findApprovalAndApprove(approval: RequestersMissionApprovalDto) {
        const missionApproval = this.missionApprovals.find(
            a => a.approval.id === approval.id
        );
        this.approveMission(missionApproval);
    }

    approveMission(approval: MissionApprovalWithAuthority) {
        if (
            this.approvalSelectionPopover != null &&
            this.approvalSelectionPopover.isOpen
        ) {
            this.approvalSelectionPopover.hide();
        }
        if (
            !this.useLegacyApprovals &&
            (this.combinedStatus === CombinedMissionStatus.DRAFT ||
                this.combinedStatus === CombinedMissionStatus.SUBMITTED)
        ) {
            this.missionDetailsService.organisation$
                .pipe(take(1))
                .subscribe(organisation => {
                    this.missionDialoguesService.showMissionEditor(
                        this.mission,
                        organisation,
                        undefined,
                        'approval'
                    );
                });
        } else {
            this.missionDetailsService.organisation$
                .pipe(take(1))
                .subscribe(organisation => {
                    this.missionDialoguesService.showMissionApproval(
                        this.mission,
                        approval.approval.id,
                        approval
                    );
                });
        }
    }

    acceptApproval(approval: MissionApprovalWithAuthority) {
        this.missionDialoguesService.showAcceptMissionApproval(
            approval,
            this.organisation,
            this.mission.timeZone as string
        );
    }

    deleteMission() {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete Mission',
                'Do you want to delete this mission? This can not be recovered',
                'Delete',
                () =>
                    firstValueFrom(
                        this.missionService.deleteMission(
                            this.missionId$.getValue()
                        )
                    ).catch((error: FlyFreelyError) => {
                        this.logging.error(
                            error,
                            `Error deleting: ${error.message}`
                        );
                        return Promise.reject(error);
                    })
            )
            .then(results => {
                this.logging.success('Mission deleted');
                this.modal.hide();
            }, DO_NOTHING);
    }

    isResolved(status: RequestersMissionApprovalDto.Status) {
        return isApprovalResolved(status);
    }

    showCraft(craft: RpaDto) {
        this.rpaDialoguesService.showCraftDetails(
            craft.id,
            this.mission.organisationId,
            false
        );
    }

    showEquipment(equipment: SimpleResourceDetails) {
        this.missionDetailsService.organisation$
            .pipe(take(1))
            .subscribe(organisation =>
                this.equipmentDialoguesService.showEquipmentDetails(
                    equipment.id,
                    organisation,
                    false
                )
            );
    }

    showPilot(pilot: PersonDto) {
        this.personnelDialoguesService.showPersonDetailsDialogue(
            pilot.id,
            this.mission.organisationId,
            false
        );
    }

    updateFieldAppOperator(personId?: number) {
        this.missionService
            .updateFieldAppOperator(this.mission.id, {
                missionId: this.mission.id,
                personId
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: updatedMission => {
                    this.logging.success('Field app operator updated');
                    this.fieldAppOperatorId =
                        updatedMission.fieldAppOperatorId ?? null;
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error updating field app operator: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    updateFilingFields() {
        const canUseProjects = hasFeatureFlag(
            this.organisation,
            FEATURE_PROJECTS
        );
        const canUseWorkgroups = hasFeatureFlag(
            this.organisation,
            FEATURE_WORKGROUPS
        );

        const projectsPromise = canUseProjects
            ? this.projectsService
                  .find(this.organisation.id, false)
                  .pipe(takeUntil(this.ngUnsubscribe$))
            : of([]);

        const workgroupsPromise = canUseWorkgroups
            ? this.workGroupsService
                  .findWorkgroups(this.organisation.id)
                  .pipe(takeUntil(this.ngUnsubscribe$))
            : of([]);

        combineLatest([projectsPromise, workgroupsPromise])
            .pipe(take(1))
            .subscribe({
                next: ([projects, workgroups]) => {
                    const fields: FormlyFieldConfig[] = [
                        {
                            key: 'name',
                            type: 'input',
                            props: {
                                label: 'Mission Name',
                                required: true
                            }
                        }
                    ];

                    if (canUseProjects && projects.length > 0) {
                        fields.push({
                            key: 'projectId',
                            type: 'ng-select',
                            props: {
                                label: 'Project',
                                required: false,
                                options: projects.map(p => ({
                                    value: p.id,
                                    label: p.name
                                }))
                            }
                        });
                    }

                    if (canUseWorkgroups && workgroups.length > 0) {
                        fields.push({
                            key: 'workgroupId',
                            type: 'ng-select',
                            props: {
                                label: 'Workgroup',
                                required: false,
                                options: workgroups
                                    .filter(w => !w.archived)
                                    .map(w => ({
                                        value: w.id,
                                        label: w.name
                                    }))
                            }
                        });
                    }

                    const initialData: Partial<UpdateMissionFilingFieldsCommand> =
                        {
                            name: this.mission.name,
                            projectId: this.mission.project?.id,
                            workgroupId: this.mission.workgroup?.id
                        };

                    this.commonDialoguesService
                        .showFormlyDialogue<
                            UpdateMissionFilingFieldsCommand,
                            DisplayableMissionDto
                        >(
                            'Edit Filing Fields',
                            'Save',
                            true,
                            true,
                            fields,
                            initialData,
                            (data: UpdateMissionFilingFieldsCommand) =>
                                firstValueFrom(
                                    this.missionService
                                        .updateFilingFields(
                                            this.mission.id,
                                            data
                                        )
                                        .pipe(
                                            tap(updatedMission => {
                                                this.logging.success(
                                                    'Mission filing fields updated'
                                                );
                                                this.mission = updatedMission;
                                            })
                                        )
                                )
                        )
                        .catch(DO_NOTHING);
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error loading filing fields data: ${error.message}`
                    )
            });
    }

    onFullscreenRequested() {
        this.fullScreenService.toggleFullScreen(this.map);
    }

    refreshRibbon() {
        this.missionRibbons.refreshData();
    }

    openConformanceTab() {
        this.tabset.tabs.forEach((val: TabDirective, i) => {
            if (val.heading === 'Conformance') {
                this.tabset.tabs[i].active = true;
            } else {
                this.tabset.tabs[i].active = false;
            }
        });
    }

    analyseFlightLog(flight: SortieDto, flightLog: FlightLogFileDto) {
        this.flightLogsDialoguesService.showFlightLog(
            flightLog.id,
            this.mission.organisationId,
            this.mission,
            flight
        );
    }
}
